import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {TexVoronoiSockets} from "#material/nodes/texture/tex-voronoi/definition"
import {texVoronoiDefinition} from "#material/nodes/texture/tex-voronoi/definition"
import {cyclesFeature, cyclesMetric} from "#material/nodes/texture/tex-voronoi/helpers"
import type {VoronoiTextureDistance, VoronoiTextureFeature} from "#material/nodes/texture/tex-voronoi/types"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import {threeRGBColor, threeValue, threeVec3} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {VoronoiTextureNode} from "#material/cycles/types"
import {cyclesDimensions} from "#material/nodes/texture/tex-noise/helpers"

@registerMaterialGraphNode
export class TexVoronoi extends MaterialNode<TexVoronoiSockets> {
    public override get definition() {
        return texVoronoiDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TexVoronoiSockets>> => {
        const {logger} = evaluator.context
        logger.warn("WARNING: Tex voronoi node not fully implemented!")

        return {distance: threeValue(0), color: threeRGBColor({r: 0, g: 0, b: 0}), position: threeVec3({x: 0, y: 0, z: 0})}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TexVoronoiSockets>> => {
        const voronoiTextureNode: VoronoiTextureNode = {
            type: "voronoi_texture",
            inputs: await evaluator.getAll({
                detail: undefined,
                exponent: this.parameters.inputs.exponent,
                lacunarity: undefined,
                randomness: this.parameters.inputs.randomness,
                roughness: this.parameters.inputs.roughness,
                scale: this.parameters.inputs.scale,
                smoothness: this.parameters.inputs.smoothness,
                vector: this.parameters.inputs.vector,
                w: this.parameters.inputs.w,
            }),
            parameters: {
                exponent: this.parameters.settings.exponent,
                feature: cyclesFeature(this.parameters.settings.feature as VoronoiTextureFeature),
                randomness: this.parameters.settings.randomness,
                roughness: this.parameters.settings.roughness,
                smoothness: this.parameters.settings.smoothness,
                w: this.parameters.settings.w,
                metric: cyclesMetric(this.parameters.settings.distance as VoronoiTextureDistance),
                detail: this.parameters.settings.detail,
                dimensions: cyclesDimensions(this.parameters.settings.voronoiDimensions),
                lacunarity: this.parameters.settings.lacunarity,
                scale: this.parameters.settings.scale,
                use_normalize: this.parameters.settings.normalize,
            },
            resources: {},
        }

        return {color: [voronoiTextureNode, "color"], distance: [voronoiTextureNode, "distance"], position: [voronoiTextureNode, "position"]}
    }
}
